import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Percent, ShoppingBag, X, ChevronRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { whenFeedReady } from "@/lib/lowPriority";
import { resolveMediaUrl } from "@/components/FileUpload";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { avatarSrc } from "@/lib/defaults";
import { ListingPrice } from "@/components/marketplace/Promo";

// Lot 695 — « Promo du jour » : carte intelligente du fil (rotation / délai / « × » / stats comme les autres) qui
// montre l'annonce la plus remisée de la Marketplace. Tap → la fiche (/marketplace?item=…) ; lien → section « Promos ».
const RED = "#D64545";
const RED_SOFT = "#FDECEC";

export const usePromoOfTheDay = (enabled = true) => {
  const [data, setData] = useState({ listing: null, count: 0 });
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!enabled) { setData({ listing: null, count: 0 }); setLoaded(true); return undefined; }
    let alive = true;
    const cancel = whenFeedReady(() => {
      api.get("/marketplace/promo-day")
        .then(({ data: d }) => { if (alive) setData({ listing: d.listing || null, count: d.count || 0 }); })
        .catch(() => {})
        .finally(() => { if (alive) setLoaded(true); });
    });
    return () => { alive = false; cancel(); };
  }, [enabled]);
  return { ...data, loaded };
};

export const PromoOfTheDayCard = ({ data, onDismiss = null }) => {
  const { t, lang } = useApp();
  const nav = useNavigate();
  const it = data?.listing;
  if (!it?.promo) return null;
  const open = () => nav(`/marketplace?item=${encodeURIComponent(it.id)}`);
  return (
    <div
      role="link" tabIndex={0} onClick={open} onKeyDown={(e) => { if (e.key === "Enter") open(); }}
      className="yalola-card p-4 space-y-2.5 cursor-pointer hover:bg-[color:var(--yalola-surface-alt)] transition-colors relative"
      style={{ borderColor: `${RED}55`, backgroundImage: `radial-gradient(circle at 100% 0%, ${RED_SOFT} 0, transparent 140px)` }}
      data-testid="promo-day-card" data-listing-id={it.id}
    >
      {onDismiss && (
        <button onClick={(e) => { e.stopPropagation(); onDismiss(); }} className="absolute top-2 right-2 h-7 w-7 grid place-items-center rounded-full hover:bg-[rgba(0,0,0,0.06)]" aria-label={t("common.close", "Fermer")} data-testid="promo-day-dismiss">
          <X size={14} />
        </button>
      )}
      <div className="flex items-center gap-2 pr-8">
        <span className="h-8 w-8 rounded-full grid place-items-center shrink-0" style={{ background: RED_SOFT }}><Percent size={15} color={RED} strokeWidth={3} /></span>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold uppercase tracking-wide" style={{ color: RED }}>{t("market.promo_day", "Promo du jour")} · Marketplace</div>
          <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{t("market.promo_day_hint", "La meilleure remise de la Marketplace")}</div>
        </div>
      </div>
      <div className="flex gap-3 items-center">
        {it.images?.[0] ? (
          <img src={resolveMediaUrl(it.images[0])} alt="" className="h-20 w-20 rounded-xl object-cover shrink-0" />
        ) : (
          <div className="h-20 w-20 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--yalola-surface-alt)" }}><ShoppingBag size={24} className="opacity-30" /></div>
        )}
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm line-clamp-2" data-testid="promo-day-title">{it.title}</div>
          <ListingPrice it={it} t={t} lang={lang} />
          {it.seller && (
            <div className="text-xs flex items-center gap-1.5 mt-1" style={{ color: "var(--yalola-text-muted)" }}>
              <Avatar className="h-4 w-4"><AvatarImage src={avatarSrc(it.seller, 32)} /><AvatarFallback className="text-[8px]">{it.seller.name?.[0]}</AvatarFallback></Avatar>
              <span className="truncate">{it.seller.name}</span>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 pt-1">
        <span className="text-xs font-semibold inline-flex items-center gap-0.5" style={{ color: RED }} data-testid="promo-day-open">{t("market.see_listing", "Voir l'annonce")} <ChevronRight size={14} /></span>
        {data.count > 1 && (
          <button onClick={(e) => { e.stopPropagation(); nav("/marketplace?promo=1"); }} className="text-xs font-semibold hover:underline" style={{ color: "var(--yalola-primary)" }} data-testid="promo-day-all">
            {t("market.all_promos", "Toutes les promos ({n})").replace("{n}", String(data.count))}
          </button>
        )}
      </div>
    </div>
  );
};
